import { computed, onBeforeUnmount, ref, StyleValue, watch } from 'vue';
import { debounce } from 'lodash-es';
import { type CurrentInstance } from '@/common/stores/instance';
import type { BreadCrumbMenuInfo, BreadCrumbMode, MenuPosition } from '../breadCrumbs.types';
import { useInternational } from '@/common/locale';

export interface Props {
  menuPosition: MenuPosition | null;
  isMenuToggle: boolean;
  loading: boolean;
  menuList?: BreadCrumbMenuInfo;
  currentBreadCrumbId?: string;
  mode?: BreadCrumbMode;
  currentInstance?: CurrentInstance;
}

export interface Emit {
  (e: 'update:searchDashboardWord', word: string): void;
  (e: 'leave', event: MouseEvent): void;
  (e: 'close'): void;
}

const useInstanceModal = (props: Props) => {
  const multiViewChildren = ref<BreadCrumbMenuInfo>();
  const multiViewChildrenStyle = ref<StyleValue>();

  const instanceMultiViewExpand = (menu?: BreadCrumbMenuInfo, position?: { top: number }) => {
    multiViewChildren.value = menu;

    if (!position) {
      multiViewChildrenStyle.value = {
        top: 0,
      };
      return;
    }

    const menuTop = parseInt(props.menuPosition?.top ?? '', 10);
    const childTop = position?.top ?? 0;
    multiViewChildrenStyle.value = {
      top: `${childTop - menuTop}px`,
    };
  };

  return {
    multiViewChildren,
    multiViewChildrenStyle,
    instanceMultiViewExpand,
  };
};

const useSearchModal = () => {
  const searchWord = ref('');
  const debouncedSearchWord = ref('');

  const updateDebouncedSearchWord = debounce((word: string) => {
    debouncedSearchWord.value = word;
  }, 200);

  watch(searchWord, (word) => {
    updateDebouncedSearchWord(word);
  });

  onBeforeUnmount(() => {
    updateDebouncedSearchWord.cancel();
  });

  return {
    searchWord,
    debouncedSearchWord,
  };
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { multiViewChildren, multiViewChildrenStyle, instanceMultiViewExpand } =
    useInstanceModal(props);
  const { searchWord, debouncedSearchWord } = useSearchModal();

  const filteredMenuList = computed<BreadCrumbMenuInfo>(() => {
    // 일부 메뉴(예: Application 성능 분석)는 children 대신 subMenuList에만 하위 메뉴가 들어있음
    const rawChildren = props.menuList?.children ?? props.menuList?.subMenuList;

    return {
      ...props.menuList,
      children: rawChildren?.filter((menu) => {
        if (!['dashboard', 'instance'].includes(props.mode ?? '')) return true;
        if (!debouncedSearchWord.value) return true;

        return menu.text
          .toLocaleLowerCase()
          .includes(debouncedSearchWord.value.toLocaleLowerCase().trim());
      }),
    };
  });

  const onMenuMouseLeave = (e: MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget.dataset.breadCrumbId === props.currentBreadCrumbId) return;

    emit('leave', e);
  };

  const onMenuCloseClick = () => {
    emit('close');
    multiViewChildren.value = undefined;
    multiViewChildrenStyle.value = undefined;
  };

  watch(filteredMenuList, () => {
    multiViewChildren.value = undefined;
    multiViewChildrenStyle.value = undefined;
  });

  return {
    t,
    multiViewChildren,
    multiViewChildrenStyle,

    searchWord,
    filteredMenuList,

    onMenuMouseLeave,
    onMenuCloseClick,
    instanceMultiViewExpand,
  };
};
